import { formatNumber } from '../../lib/format';

export interface Segment {
  label: string;
  value: number;
  /** A CSS custom-property reference, e.g. `var(--series-1)`. */
  color: string;
}

/**
 * Parts of a whole, as one horizontal bar.
 *
 * A bar rather than a donut: at this size a reader compares lengths far
 * more accurately than angles, and the labels sit on one baseline instead
 * of orbiting a circle.
 *
 * Colour here encodes identity, so the three hues are the validated
 * categorical slots (all-pairs deutan ΔE 9.2, normal-vision 24.0 on this
 * surface). Every segment is also named in the legend below, so identity
 * never rests on colour alone — which is additionally the relief that the
 * aqua slot's sub-3:1 contrast requires.
 */
export function CompositionBar({
  segments,
  total,
}: {
  segments: Segment[];
  total: number;
}) {
  const sum = total || segments.reduce((n, s) => n + s.value, 0) || 1;

  return (
    <div>
      <div className="flex h-7 w-full gap-[2px] overflow-hidden rounded">
        {segments
          .filter((s) => s.value > 0)
          .map((s) => {
            const pct = (s.value / sum) * 100;
            return (
              <div
                key={s.label}
                className="group relative h-full min-w-[3px]"
                style={{ width: `${pct}%`, backgroundColor: s.color }}
              >
                <div
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded border border-rule bg-surface px-2 py-1 text-[0.6875rem] text-ink shadow-[var(--shadow-overlay)] group-hover:block"
                >
                  {s.label} · {formatNumber(s.value)} · {pct.toFixed(1)}%
                </div>
              </div>
            );
          })}
      </div>

      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {segments.map((s) => (
          <li key={s.label} className="flex items-center gap-1.5 text-xs">
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-ink-2">{s.label}</span>
            <span className="tabular font-medium text-ink">{formatNumber(s.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
